import React from 'react'
import { Link } from 'react-router-dom'
import {
  Truck,
  Plane,
  TrainFront,
  Ship,
  Warehouse,
  Handshake,
  Network,
  MapPin,
  ThermometerSnowflake,
  ShieldCheck,
  Clock3,
  Globe2,
  Headset,
  ArrowUpRight,
  Zap
} from 'lucide-react'
import multimodel from '../assets/images/data/multimodel.png'

const services = [
  {
    icon: Truck,
    title: 'Surface Express',
    subtitle: 'Road Transportation',
    link: '/services/surface-express'
  },
  {
    icon: Warehouse,
    title: 'Warehousing',
    subtitle: 'Storage & Distribution',
    link: '/services/warehousing'
  },
  {
    icon: TrainFront,
    title: 'Rail Cargo',
    subtitle: 'Rail Transportation',
    link: '/services/rail-cargo'
  },
  {
    icon: Handshake,
    title: 'Contract Logistics',
    subtitle: 'End-to-End Solutions',
    link: '/services/contract-logistics'
  },
  {
    icon: Plane,
    title: 'Air Freight',
    subtitle: 'Air Cargo Services',
    link: '/services/air-freight-forwarding'
  },
  {
    icon: Network,
    title: 'Supply Chain',
    subtitle: 'Smart Logistics',
    link: '/services/supply-chain-management'
  },
  {
    icon: Ship,
    title: 'Ocean Freight',
    subtitle: 'Global Shipping',
    link: '/services/international'
  },
  {
    icon: MapPin,
    title: 'Last-Mile',
    subtitle: 'Door-to-Door Delivery',
    link: '/services/last-mile-delivery'
  },
  {
    icon: ThermometerSnowflake,
    title: 'Cold Chain',
    subtitle: 'Temperature Control',
    link: '/services/cold-chain'
  }
]

const benefits = [
  {
    icon: ShieldCheck,
    title: 'Trusted Partner',
    subtitle: 'Building long-term partnerships'
  },
  {
    icon: Clock3,
    title: 'On-Time Delivery',
    subtitle: 'Every time, everywhere'
  },
  {
    icon: Globe2,
    title: 'Pan India Network',
    subtitle: 'Reaching every corner'
  },
  { icon: Headset, title: '24/7 Support', subtitle: 'Always here for you' }
]

export default function LogisticsHomeHero () {
  return (
    <main className='relative isolate overflow-hidden bg-[#F8FAFF] font-sans text-[#11194C] py-14 sm:py-16 lg:py-6'>
      {/* Soft brand background */}
      <div className='pointer-events-none absolute -left-28 top-10 h-72 w-72 rounded-full bg-[#FFF44F]/25 blur-3xl' />
      <div className='pointer-events-none absolute right-0 top-0 h-[580px] w-[55%] rounded-bl-[180px] bg-sky-100/60 blur-2xl' />

      {/* Hero */}
      <section className='relative mx-auto grid w-[92%] max-w-[1320px] mb-4 items-center gap-8 lg:min-h-[590px] lg:grid-cols-[0.92fr_1.08fr]'>
        {/* Copy */}

        {/* IMAGE — imported from your assets folder */}
        <div className='relative mx-auto min-h-[320px] w-full max-w-[760px] sm:min-h-[440px] lg:min-h-[560px]'>
          <div className='absolute inset-0 overflow-hidden rounded-[28px] border-[5px] border-white bg-white shadow-[0_25px_70px_rgba(17,25,76,0.20)] sm:rounded-[38px] lg:rounded-[48px]'>
            <img
              src={multimodel}
              alt='S Infinite Express multimodal logistics across road, air, rail and ocean'
              className='h-full min-h-[320px] w-full object-cover object-center sm:min-h-[440px] lg:min-h-[560px]'
            />
            <div className='pointer-events-none absolute inset-0 bg-gradient-to-t from-[#07194D]/25 via-transparent to-[#07529A]/5' />
            <div className='pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/30' />
          </div>

          {/* Floating info cards — real HTML text */}
          <div className='absolute left-1 top-5 z-10 flex items-center gap-2.5 rounded-2xl border border-white/80 bg-white/95 p-3 shadow-[0_12px_35px_rgba(7,25,77,0.16)] backdrop-blur sm:left-0 sm:top-8 sm:p-3.5'>
            <span className='flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFF44F] text-[#11194C]'>
              <Zap size={21} />
            </span>
            <span>
              <span className='block text-xs font-extrabold text-[#11194C]'>
                Moving Business Forward
              </span>
              <span className='mt-0.5 block text-[10px] text-slate-500'>
                Reliable freight movement
              </span>
            </span>
          </div>

          <div className='absolute bottom-5 right-1 z-10 flex items-center gap-2.5 rounded-2xl border border-white/80 bg-white/95 p-3 shadow-[0_12px_35px_rgba(7,25,77,0.16)] backdrop-blur sm:bottom-8 sm:right-0 sm:p-3.5'>
            <span className='flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-[#07529A]'>
              <Globe2 size={21} />
            </span>
            <span>
              <span className='block text-xs font-extrabold text-[#11194C]'>
                Global Network
              </span>
              <span className='mt-0.5 block text-[10px] text-slate-500'>
                Connected logistics
              </span>
            </span>
          </div>
        </div>

        <div className='relative z-20 lg:pl-10'>
          <div className='flex items-center justify-center lg:justify-start flex-wrap gap-2 sm:gap-3 text-[#07529A] font-semibold'>
            <Truck size={18} />
            <span className='text-xs sm:text-sm'>Road</span>
            <span className='text-gray-300'>/</span>
            <Plane size={18} />
            <span className='text-xs sm:text-sm'>Air</span>
            <span className='text-gray-300'>/</span>
            <TrainFront size={18} />
            <span className='text-xs sm:text-sm'>Rail</span>
          </div>

          <h1 className='max-w-[790px] text-[40px] font-black leading-[0.99] tracking-[-0.055em] text-[#11194C] sm:text-4xl md:text-5xl xl:text-[70px]'>
            One Network.
            <span className='mt-2 block bg-gradient-to-r from-[#0875D1] to-[#13A1E8] bg-clip-text text-transparent'>
              Multiple Modes.
            </span>
            <span className='mt-2 block'>Infinite Possibilities.</span>
          </h1>

          <div className='mt-5 flex items-center gap-2'>
            <span className='h-1 w-16 rounded-full bg-[#FFF44F]' />
            <span className='h-1 w-5 rounded-full bg-[#07529A]/20' />
          </div>

          <p className='mt-5 max-w-[550px] text-sm leading-7 text-slate-600 sm:text-base sm:leading-8'>
            Move your business forward with intelligent multimodal logistics.
            Combining road, air and rail transportation, we create flexible,
            efficient and reliable freight solutions across India.
          </p>

          {/* Feature highlights */}
          <div className='grid grid-cols-2 gap-3 sm:gap-4 mt-7 max-w-lg mx-auto lg:mx-0 text-left'>
            <div className='border-l-[3px] border-[#FFF44F] pl-3 sm:pl-4'>
              <h4 className='text-sm sm:text-base font-bold text-[#1B1A4B]'>
                Smart Connectivity
              </h4>
              <p className='text-xs sm:text-sm text-gray-500 mt-1'>
                Multiple transport modes
              </p>
            </div>

            <div className='border-l-[3px] border-[#07529A] pl-3 sm:pl-4'>
              <h4 className='text-sm sm:text-base font-bold text-[#1B1A4B]'>
                Efficient Movement
              </h4>
              <p className='text-xs sm:text-sm text-gray-500 mt-1'>
                Optimized freight solutions
              </p>
            </div>
          </div>

          {/* Calls to action */}
          <div className='mt-7 flex flex-wrap items-center gap-4'>
            <Link
              to='/our-services'
              className='group inline-flex items-center gap-3 rounded-full bg-[#07194D] py-2 pl-6 pr-2 text-sm font-bold text-white shadow-[0_12px_25px_rgba(7,25,77,0.22)] transition duration-300 hover:-translate-y-1 hover:bg-[#07529A] sm:text-base'
            >
              Explore Our Services
              <span className='flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF44F] text-[#07194D] transition group-hover:rotate-45'>
                <ArrowUpRight size={19} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* All nine services are actual text and icons */}
      <section className='relative z-20 mx-auto w-[92%] max-w-[1320px] mb-4'>
        <div className='rounded-[25px] border border-white bg-white/95 p-3 shadow-[0_16px_50px_rgba(17,25,76,0.13)] backdrop-blur-xl sm:p-4'>
          <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-10'>
            <div className='flex min-h-[104px] flex-col justify-center px-2 py-2 sm:min-h-[120px]'>
              <h2 className='whitespace-nowrap text-sm font-black text-[#11194C] sm:text-base'>
                Our Services
              </h2>
              <p className='mt-1 text-[11px] leading-2 text-slate-600'>
                End-to-End Logistics
              </p>
              <Link
                to='/our-services'
                className='mt-2 text-[11px] font-bold text-[#07529A] hover:underline'
              >
                View All Services →
              </Link>
            </div>

            {services.map(({ icon: Icon, title, subtitle, link }) => (
              <Link
                key={title}
                to={link}
                className='group flex min-h-[104px] flex-col items-center justify-center gap-2 rounded-2xl px-1 py-2 text-center transition duration-300 hover:-translate-y-1 hover:bg-[#FFFDE7] hover:shadow-md sm:min-h-[120px]'
              >
                <span className='flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-[#07529A] transition group-hover:bg-[#FFF44F] group-hover:text-[#11194C]'>
                  <Icon size={22} strokeWidth={2.2} />
                </span>

                <span className='text-[10px] font-extrabold leading-snug text-[#11194C] sm:text-[11px]'>
                  {title}
                  <span className='mt-0.5 block font-semibold'>{subtitle}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className='relative overflow-hidden bg-[#07194D]'>
        <div className='pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-blue-500/20 blur-3xl' />
        <div className='relative mx-auto grid max-w-[1500px] grid-cols-2 gap-y-5 px-5 py-7 sm:px-8 md:grid-cols-4 lg:px-12'>
          {benefits.map(({ icon: Icon, title, subtitle }) => (
            <div
              key={title}
              className='flex items-center justify-center gap-3 border-white/20 px-2 sm:justify-start sm:border-r sm:px-5 last:border-r-0'
            >
              <Icon size={31} className='shrink-0 text-[#FFF44F]' />
              <span>
                <span className='block text-xs font-extrabold text-white sm:text-sm'>
                  {title}
                </span>
                <span className='mt-1 block text-[10px] text-blue-100 sm:text-xs'>
                  {subtitle}
                </span>
              </span>
            </div>
          ))}
        </div>
      </section>
    </main>
  )
}
